import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Exhibitor, exhibitorProps } from 'globals/store/exhibitors.data';
import { ShareWrapper } from './share-wrapper.component';
import { JoiFile } from 'globals/store/files.model';
import { CustomQuestion } from 'globals/store/events.data';
import { JoiTemplate } from 'globals/store/publish.data';
import { CommonModule } from '@angular/common';
import { ImageUploaderComponent } from 'src/app/components/wrappers/image-uploader.component';
import { MutatingInputComponent } from 'src/app/components/joi-input/mutating-input.component';
import { AddManageWrapperComponent } from 'src/app/components/wrappers/add-manage-wrapper.component';
import { SocialLinksComponent } from 'src/app/components/social-links/social-links.component';
import { InputEditorComponent } from 'src/app/components/joi-input/input-editor';
import { FileLinkComponent } from 'src/app/components/file-handler/file-link.component';
import { MatButtonModule } from '@angular/material/button';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { CustomQuestionsComponent } from 'src/app/components/entities/custom-questions.component';
import { Subject } from 'rxjs';

@Component({
    selector: 'share-edit-exhibitor-form',
    template: `
<div class="container" *ngIf="!!exhibitor && !submitted">
  <div class="event-name" *ngIf="eventName">{{ exhibitor.name }} Profile in {{ eventName }}</div>
  <div class="instructions" *ngIf="!!instructions" [innerHTML]="instructions"></div>
  <div class="top">
    <div class="photos">
      <image-uploader 
        #logoEditor 
        [id]="exhibitor.id || ''" 
        [maxSize]="1024*1024*10" 
        [public]="true" 
        [isLogo]="true"
        [photoUrl]="exhibitor.fullLogoUrl"
        [publishCode]="publishCode"
        [hints]="imageHints"
        [eventId]="eventId"
        [placeholder]="'Add logo'" 
        [uploadText]="'UPLOAD LOGO'"
        [type]="'exhibitor'" 
        [subtype]="'logo'"
      ></image-uploader>
    </div>
    <div class="fields">
      <mutating-input [object]="exhibitor" [prop]="props.name" placeholder="Exhibitor name"></mutating-input>
      <add-manage-wrapper class="social-open" [hideHRule]="true" wrapperTitle="SOCIAL" [openLabel]="!exhibitor.social?.length ? '+ ADD A SOCIAL LINK' : 'MANAGE SOCIAL LINKS'" (open)="openSocialLinks($event, exhibitor)">
        <social-links *ngIf="exhibitor?.social?.length" [socialLinks]="exhibitor.social || []"></social-links>
      </add-manage-wrapper>
      <h5>Description</h5>
      <input-editor 
        class="placeholder-no-italics"
        [object]="exhibitor" 
        [forceSave]="forceSave" 
        [prop]="props.description" 
        [placeholder]="'Click to add description...'"
      ></input-editor>

      <mutating-input [object]="exhibitor" [prop]="props.callToAction" placeholder="Call to action text"></mutating-input>
      <mutating-input 
        [object]="exhibitor" 
        [prop]="props.callToActionUrl" 
        [placeholder]="'Call to action URL (Must start with http or https)'"
        [error]="CTAInvalid ? 'URL must start with http:// or https://' : ''"
        (textChange)="checkLinkOK(); changed = true;"
        (unfocus)="checkLink(); changed = true;"
      ></mutating-input>
    </div>
  </div>
  <div class="bottom">

    <ng-container *ngIf="oldFiles?.length">
      <div class="am-title">EXISTING ASSETS</div>
      <file-link *ngFor="let file of oldFiles"
        [file]="file"
        [onlyLink]="true"
      ></file-link>   
    </ng-container>

    <mutating-input [object]="exhibitor" [prop]="props.contactName" placeholder="Exhibitor contact name"></mutating-input>
    <mutating-input [object]="exhibitor" [prop]="props.contactNumber"  placeholder="Exhibitor contact phone" ></mutating-input>
    <mutating-input [object]="exhibitor" [prop]="props.contactEmail" placeholder="Exhibitor contact email" ></mutating-input>
    <h5>Exhibitor contact address</h5>
    <input-editor 
      class="placeholder-no-italics"
      [object]="exhibitor"
      [forceSave]="forceSave" 
      [prop]="'contactFullAddress'" 
      [placeholder]="'Exhibitor contact address...'"
    ></input-editor>

    <custom-questions
      [entity]="exhibitor"
      [questions]="customQuestions"
      [showMandatoryWarnings]="showMandatoryWarnings"
      [shareView]="true"
      (updateQuestion)="updateCustomQuestion($event, exhibitor)"
    ></custom-questions>

    <button mat-button *ngIf="{ processing: processing || logoEditor.imageChanging } as vars" color="primary" (click)="submit()" 
      [disabled]="vars.processing || invalid" [ngClass]="{ processing: vars.processing }">
      <div class="button_content">
        SUBMIT
        <loader *ngIf="!invalid" icon="button_wheel"></loader>
      </div>
    </button>
    <div class="error-holder" *ngIf="error" [innerHTML]="helpers.strWithBreak(error)"></div>
  </div>
</div>
    `,
    styleUrls: ['./share.editPerformer.scss'],
    styles: [`
      add-assets { margin-bottom: 10px; }
      ::ng-deep add-assets .add_btn { text-align: left !important; }
    `],
    standalone: true,
    imports: [ImageUploaderComponent, CustomQuestionsComponent, MutatingInputComponent, AddManageWrapperComponent, SocialLinksComponent, InputEditorComponent, CustomQuestionsComponent, FileLinkComponent, MatButtonModule, LoaderComponent, CommonModule]
})
export class ExhibitorEditFormComponent extends ShareWrapper<ExhibitorEditFormComponent> {
    @ViewChild('logoEditor') logoEditor

    @Input() topTitle: string
    @Input() eventId: string
    @Input() exhibitor: Exhibitor
    @Input() publishCode // in the case of 
    @Input() customQuestions: CustomQuestion[] = []
    @Input() error
    @Input() processing: boolean
    @Input() callToSubmit: boolean
    @Input() instructions: string
    @Input() template: JoiTemplate
    forceSave: Subject<void> = new Subject<void>()
    
    @Output() onSubmit: EventEmitter<Exhibitor> = new EventEmitter()

    props = exhibitorProps
    newFiles: JoiFile[] = []
    oldFiles: JoiFile[]
    eventName: string
    showMandatoryWarnings = false;

    invalid = false
    changed = false
    CTAInvalid = false

    checkLinkOK() { // so can show it's okay after typing a valid start
      const url = this.exhibitor.callToActionUrl
      if (!url || this.helpers.urlOk(url)) this.CTAInvalid = false
    }

    checkLink() {
      const url = this.exhibitor.callToActionUrl
      this.CTAInvalid = !!url && !this.helpers.urlOk(url)
    }

    async submit() {
      // CHECK FOR MANDATORY FIELDS
      this.forceSave.next();
      const allRequirementsCompleted = await this.publicService.checkForMandatory(this.customQuestions, this.exhibitor.customQuestions);
      if(!allRequirementsCompleted) {
        this.showMandatoryWarnings = true;
        return;
      }
      if (this.logoEditor.deleteDeletedPhotos()) {
        this.exhibitor.logoUrl = this.logoEditor.tempThumbUrl
        this.exhibitor.fullLogoUrl = this.logoEditor.tempUrl
      }

      this.onSubmit.emit(this.exhibitor)
    }

}

